/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useState, useCallback } from 'react'
import { TonConnectButton, useTonAddress } from '@tonconnect/ui-react'
import { BackendTokenContext } from '../auth/BackendTokenContext'
import {
  Gift,
  AlertCircle,
  BadgeCheck,
  Share2,
  Copy,
  ChevronDown,
} from 'lucide-react'
import {
  collection,
  DocumentData,
  limit,
  query,
  QuerySnapshot,
} from 'firebase/firestore'
import { db } from '@/helpers/firebase'
import { callProcessReferral } from '@/helpers/api'
import { ReferralRewardModal } from './Modals/ReferralRewardModal'
import { useFirestoreCollectionCache } from '@/hooks/useFirestoreCache'
import * as referrals from 'common/schema/users/referrals'
import '@/screens/Wallet.css'

export const Wallet: FC = () => {
  const { token } = useContext(BackendTokenContext)
  const walletAddress = useTonAddress()
  const [referralCode, setReferralCode] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRewardModal, setShowRewardModal] = useState(false)
  const [shareSuccess, setShareSuccess] = useState(false)
  const [copySuccess, setCopySuccess] = useState(false)
  const [isStatsExpanded, setIsStatsExpanded] = useState(true)
  const [rewardData, setRewardData] = useState<{
    referrerReward?: number
    refereeReward?: number
    milestone?: {
      count: number
      reward: number
    }
  } | null>(null)

  // Memoize the transform function
  const transformReferralData = useCallback(
    (snapshot: QuerySnapshot<DocumentData>) => {
      const docs: referrals.ReferralData[] = []
      snapshot.forEach((doc) => {
        docs.push(doc.data() as referrals.ReferralData)
      })
      return docs
    },
    []
  )

  // Use the memoized transform function
  const { data: referralDocs = [] } =
    useFirestoreCollectionCache<referrals.ReferralData>(
      walletAddress
        ? query(
            collection(db, referrals.collectionPath(walletAddress)),
            limit(1)
          )
        : null,
      `referral-${walletAddress}`,
      transformReferralData
    )

  // Since we know there's only one referral doc per user, get the first one
  const referralData = referralDocs[0]

  const handleReferralSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!referralCode.trim() || !walletAddress) return

    setIsProcessing(true)
    setError(null)

    try {
      const result = await callProcessReferral({
        referralCode: referralCode.trim().toUpperCase(),
      })

      if (result.success) {
        setRewardData({
          refereeReward: result.response.data.refereeReward,
        })
        setShowRewardModal(true)
        setReferralCode('')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to process referral code')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCopy = useCallback(async () => {
    if (!referralData?.referralCode) return

    try {
      await navigator.clipboard.writeText(referralData.referralCode)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Error copying code:', err)
    }
  }, [referralData?.referralCode])

  const handleShare = useCallback(async () => {
    if (!referralData?.referralCode) return

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Join Me in Card Collection Game',
          text: `Join me in collecting amazing NFT cards! Use my referral code: ${referralData.referralCode}`,
          url: window.location.origin,
        })
        setShareSuccess(true)
        setTimeout(() => setShareSuccess(false), 2000)
      } else {
        handleCopy()
      }
    } catch (err) {
      console.error('Error sharing:', err)
      handleCopy()
    }
  }, [referralData?.referralCode, handleCopy])

  return (
    <div className="wallet-container">
      <div className="wallet-content">
        <h2 className="wallet-title">Welcome to Card Collection</h2>

        {!token && !walletAddress ? (
          <>
            <p className="wallet-description">
              Connect your wallet to start collecting unique cards and earning
              rewards
            </p>
            <TonConnectButton />
          </>
        ) : !token ? (
          <div className="wallet-loading">
            <div className="wallet-spinner" />
            <p>Connecting to wallet...</p>
          </div>
        ) : (
          <div className="wallet-connected">
            <div className="wallet-connected-header">
              <BadgeCheck size={48} className="connected-icon" />
              <h3>Wallet Connected!</h3>
            </div>

            <TonConnectButton />

            {referralData && (
              <div className="user-referral-section">
                <div
                  className="referral-stats-header"
                  onClick={() => setIsStatsExpanded(!isStatsExpanded)}
                >
                  <h3>Your Referral Stats</h3>
                  <ChevronDown
                    className={`chevron ${isStatsExpanded ? 'expanded' : ''}`}
                    size={20}
                  />
                </div>

                {isStatsExpanded && (
                  <div className="referral-stats-content">
                    <div className="referral-code-display">
                      <h4>Your Referral Code</h4>
                      <div className="code-display">
                        <span className="code" onClick={handleCopy}>
                          {referralData.referralCode}
                        </span>
                        <button
                          className={`share-button ${shareSuccess || copySuccess ? 'success' : ''}`}
                          onClick={handleShare}
                        >
                          {shareSuccess || copySuccess ? (
                            <>
                              <BadgeCheck size={20} />
                              {shareSuccess ? 'Shared!' : 'Copied!'}
                            </>
                          ) : (
                            <>
                              {typeof navigator.share === 'function' ? (
                                <>
                                  <Share2 size={20} />
                                  Share
                                </>
                              ) : (
                                <>
                                  <Copy size={20} />
                                  Copy
                                </>
                              )}
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="referral-stats-grid">
                      <div className="stat-card">
                        <div className="stat-content">
                          <h5>Total Referrals</h5>
                          <p>{referralData.totalReferrals}</p>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-content">
                          <h5>Tokens Earned</h5>
                          <p>{referralData.referralTokensEarned}</p>
                        </div>
                      </div>
                    </div>

                    <div className="tier-progress">
                      <div className="tier-header">
                        <h4>Current Tier: {referralData.currentTier}</h4>
                      </div>
                      <div className="progress-bar-wrapper">
                        <div
                          className="progress-bar"
                          style={{
                            width: `${(referralData.tierProgress / referralData.nextTierAt) * 100}%`,
                          }}
                        />
                      </div>
                      <p className="tier-info">
                        {referralData.nextTierAt
                          ? `${referralData.nextTierAt - referralData.tierProgress} more referrals until next tier`
                          : 'Max tier reached!'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            <form className="referral-form" onSubmit={handleReferralSubmit}>
              <div className="referral-input-wrapper">
                <Gift size={20} className="referral-icon" />
                <input
                  type="text"
                  placeholder="Have a referral code?"
                  value={referralCode}
                  onChange={(e) => {
                    setError(null)
                    setReferralCode(e.target.value)
                  }}
                  className={error ? 'error' : ''}
                  disabled={isProcessing}
                />
              </div>

              {error && (
                <div className="referral-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="referral-submit"
                disabled={!referralCode.trim() || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="button-spinner" />
                    Processing...
                  </>
                ) : (
                  'Apply Code'
                )}
              </button>
            </form>

            <p className="wallet-hint">
              Share your referral code to earn rewards when friends join!
            </p>
          </div>
        )}

        {rewardData && (
          <ReferralRewardModal
            isOpen={showRewardModal}
            onClose={() => {
              setShowRewardModal(false)
              setRewardData(null)
            }}
            rewards={rewardData}
          />
        )}
      </div>
    </div>
  )
}
